import React, {ReactNode} from "react";
import {Command, EditorState} from "prosemirror-state";
import intl from "react-intl-universal";
import {useUserState} from "@stores/AppStateContext";

import type {ContextMenuInterface} from "../../ContextMenuInterface";

import {NumberListIcon} from "../../../../components/icons/editor/NumberListIcon";
import {BulletListIcon} from "../../../../components/icons/editor/BulletListIcon";
import {CheckListIcon} from "../../../../components/icons/editor/CheckListIcon";
import {PIcon} from "../../../../components/icons/editor/PIcon";
import {H1Icon} from "../../../../components/icons/editor/H1Icon";
import {H2Icon} from "../../../../components/icons/editor/H2Icon";
import {H3Icon} from "../../../../components/icons/editor/H3Icon";
import {ItalicIcon} from "../../../../components/icons/editor/ItalicIcon";
import {LinkIcon} from "../../../../components/icons/editor/LinkIcon";
import {HighlightIcon} from "../../../../components/icons/editor/HighlightIcon";
import {QuoteIcon} from "../../../../components/icons/editor/QuoteIcon";
import {UnderlineIcon} from "../../../../components/icons/editor/UnderlineIcon";
import {BoldIcon} from "../../../../components/icons/editor/BoldIcon";

import {isBlockActive} from "../../../../../Editor/utils/isBlockActive";
import {isMarkActive} from "../../../../../Editor/utils/isMarkActive";
import {isListActive} from "../../../../../Editor/utils/isListActive";
import type {IconProps} from "../../../../components/icons";

import {
    isWrapped
} from '../../../../../Editor/utils/wrap';

import {
    canToggleBlockTypeHeading,
    toggleBlockTypeHeading
} from '../../../../../Editor/module/commands/node/heading';

import {setListTypeBullet, setListTypeChecklist, setListTypeOrdered} from '../../../../../Editor/module/commands/node/list';
import {
    canToggleWrapBlockquote,
    toggleWrapBlockquote
} from '../../../../../Editor/module/commands/node/blockquote';
import {toggleMarkBold} from '../../../../../Editor/module/commands/marks/bold';
import {toggleMarkItalic} from '../../../../../Editor/module/commands/marks/intalic';
import {toggleMarkUnderline} from '../../../../../Editor/module/commands/marks/underline';
import {
    setBlockTypeParagraph,
    isNodeParagraph,
    nodeIsNotParagraph
} from '../../../../../Editor/module/commands/node/paragraph';
import {toggleLink} from '../../../../../Editor/module/commands/marks/link';
import {toggleHighlightMark, isHighlightMarkActive} from '../../../../../Editor/module/commands/marks/highlight';
import {ContextMenuInline} from '../../../../components/ContextMenuInline';

import {schema} from '../../../../../Editor/module/config/schema';
import {editorViewRef} from '../../../../elements/EditorReact';
import {UserSessionState} from "@stores/UserSessionState";

export interface TextToolbarProps extends ContextMenuInterface {
}

export interface ToolbarAction {
    id: string,
    Icon: React.ComponentType<IconProps>,
    action: Command,
    enable: (state: EditorState) => boolean,
    active: (state: EditorState) => boolean,
    getTooltip?: () => ReactNode,
    hotKey?: string,
}

const buttonsSet = [
    {
        id: 'block-paragraph',
        title: 'Change to paragraph',
        Icon: PIcon,
        action: setBlockTypeParagraph,
        enable: nodeIsNotParagraph,
        active: isNodeParagraph,
        getTooltip: () => intl.get('tooltip.textToolbar.paragraph').d("Paragraph"),
    },
    {
        id: 'block-heading-1',
        title: 'Change to heading level 1',
        Icon: H1Icon,
        action: toggleBlockTypeHeading(1),
        enable: canToggleBlockTypeHeading(1),
        active: isBlockActive(schema.nodes.heading, {level: 1}),
        getTooltip: () => intl.get('tooltip.textToolbar.largeHeading').d("Large Heading"),
        hotKey: "Ctrl+Shift+1"
    },
    {
        id: 'block-heading-2',
        title: 'Change to heading level 2',
        Icon: H2Icon,
        action: toggleBlockTypeHeading(2),
        enable: canToggleBlockTypeHeading(2),
        active: isBlockActive(schema.nodes.heading, {level: 2}),
        getTooltip: () => intl.get('tooltip.textToolbar.mediumHeading').d("Medium Heading"),
        hotKey: "Ctrl+Shift+2"
    },
    {
        id: 'block-heading-3',
        title: 'Change to heading level 3',
        Icon: H3Icon,
        action: toggleBlockTypeHeading(3),
        enable: canToggleBlockTypeHeading(3),
        active: isBlockActive(schema.nodes.heading, {level: 3}),
        getTooltip: () => intl.get('tooltip.textToolbar.smallHeading').d("Small Heading"),
        hotKey: "Ctrl+Shift+3"
    },
    {
        id: 'block-ordered-list',
        title: 'Wrap in ordered list',
        Icon: NumberListIcon,
        action: setListTypeOrdered,
        enable: setListTypeOrdered,
        active: isListActive(schema.nodes.list, 'ordered'),
        getTooltip: () => intl.get('tooltip.textToolbar.numberedList').d("Numbered List"),
        hotKey: "Ctrl+Shift+7"
    },
    {
        id: 'block-bullet-list',
        title: 'Wrap in bullet list',
        Icon: BulletListIcon,
        action: setListTypeBullet,
        enable: setListTypeBullet,
        active: isListActive(schema.nodes.list, 'bullet'),
        getTooltip: () => intl.get('tooltip.textToolbar.bulletedList').d("Bulleted List"),
        hotKey: "Ctrl+Shift+8"
    },
    {
        id: 'block-check-list',
        title: 'Wrap in checklist',
        Icon: CheckListIcon,
        action: setListTypeChecklist,
        enable: setListTypeChecklist,
        active: isListActive(schema.nodes.list, 'checklist'),
        getTooltip: () => intl.get('trigger-actions.task_list').d("Checklist"),
        hotKey: "Ctrl+Shift+9"
    },
    {
        id: 'bold',
        Icon: BoldIcon,
        action: toggleMarkBold,
        enable: toggleMarkBold,
        active: isMarkActive(schema.marks.bold),
        getTooltip: () => intl.get('tooltip.textToolbar.bold').d("Bold"),
        hotKey: "Ctrl+B"
    },
    {
        id: 'toggle-italic',
        title: 'Toggle italic',
        Icon: ItalicIcon,
        action: toggleMarkItalic,
        enable: toggleMarkItalic,
        active: isMarkActive(schema.marks.italic),
        getTooltip: () => intl.get('tooltip.textToolbar.italic').d("Italic"),
        hotKey: "Ctrl+I"
    },
    {
        id: 'toggle-underline',
        Icon: UnderlineIcon,
        action: toggleMarkUnderline,
        enable: toggleMarkUnderline,
        active: isMarkActive(schema.marks.underline),
        getTooltip: () => intl.get('tooltip.textToolbar.underline').d("Underline"),
        hotKey: "Ctrl+U"

    },
    {
        id: 'block-blockquote',
        title: 'Toggle blockquote wrapper',
        Icon: QuoteIcon,
        action: toggleWrapBlockquote,
        enable: canToggleWrapBlockquote,
        active: isWrapped(schema.nodes.blockquote),
        getTooltip: () => intl.get('tooltip.textToolbar.quote').d("Quote"),
        hotKey: "Ctrl+>"
    },
    /*    {
            id: 'toggle-code',
            title: 'Toggle code',
            Icon: CodeBlockIcon,
            action: toggleMarkCode,
            enable: toggleMarkCode,
            active: isMarkActive(schema.marks.code),
        },*/

    {
        id: 'toggle-link',
        title: 'Add or remove link',
        Icon: LinkIcon,
        action: toggleLink,
        enable: (state: EditorState) => !state.selection.empty,
        active: isMarkActive(schema.marks.link),
        getTooltip: () => intl.get('tooltip.textToolbar.link').d("Link"),
    },
    {
        id: 'mark',
        title: 'Add or remove link',
        Icon: HighlightIcon,
        action: toggleHighlightMark,
        enable: (state: EditorState) => !state.selection.empty,
        active: isHighlightMarkActive,
        getTooltip: () => intl.get('tooltip.textToolbar.highlight').d("Highlight"),
    },
]

export const TextToolbar = (
    {
        handleClose,
        isDark,
    }: TextToolbarProps
) => {
    const userState = useUserState()!;

    const view = editorViewRef.current;
    if (view === null) {
        return null;
    }
    const state = view.state;

    const filteredItems = [];

    for (const button of buttonsSet) {
        const isEnabled = button.enable(state);
        if (!isEnabled || filteredOutReadByOnly(userState, button.id)) {
            continue;
        }

        const isActive = button.active(state);

        filteredItems.push(
            {
                ...button,
                isActive,
                onClick: () => {
                    const actionResult = button.action(state, view?.dispatch);
                    return actionResult;
                }
            }
        )
    }

    return (
        <ContextMenuInline
            handleClose={handleClose}
            buttonsSet={filteredItems}
            isDark={isDark}
        />
    )
}

export const filteredOutReadByOnly = (userState: UserSessionState, actionId: string) => {
    if (!userState!.isCurrentUserReadOnly) {
        return false;
    }
    switch (actionId) {
        case 'comment':
            return false;
        default:
            return true;
    }
}

